<template>
  <div class="video-results">
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h2 class="page-title">
            <VideoCameraOutlined />
            {{ $t('video.videoTranslationResults') }}
          </h2>
          <p class="page-description">{{ $t('video.viewAndDownloadResults') }}</p>
        </div>
      </div>
    </div>

    <a-card class="project-info-card" :loading="loading">
      <template #title>
        <div class="card-title">
          <FileTextOutlined />
          {{ $t('video.projectInfo') }}
        </div>
      </template>

      <a-descriptions :column="2" v-if="project">
        <a-descriptions-item :label="$t('project.name')">{{ project.title }}</a-descriptions-item>
        <a-descriptions-item :label="$t('project.projectStatus')">
          <a-tag color="green">{{ getStatusText(project.status) }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('project.originalLanguage')">{{ getLanguageText(project.original_language) }}</a-descriptions-item>
        <a-descriptions-item :label="$t('video.targetLanguage')">
          {{ project.target_languages?.map(lang => getLanguageText(lang)).join(', ') || '-' }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('video.videoDuration')">{{ formatDuration(project.video_duration) }}</a-descriptions-item>
        <a-descriptions-item :label="$t('video.completionTime')">{{ formatDate(project.updated_at) }}</a-descriptions-item>
      </a-descriptions>
    </a-card>

    <div class="results-content">
      <a-row :gutter="24">
        <a-col :span="12">
          <a-card :title="$t('video.originalVideo')" class="video-card">
            <div class="video-container" v-if="project?.has_video">
              <video
                ref="videoPlayer"
                :src="getVideoUrl()"
                controls
                preload="metadata"
                class="video-player"
                @loadedmetadata="onVideoLoaded"
              >
                {{ $t('video.browserNotSupportVideo') }}
              </video>
            </div>
            <a-empty v-else :description="$t('video.noVideoFile')" />
          </a-card>
        </a-col>

        <a-col :span="12">
          <a-card :title="$t('video.subtitleFiles')" class="subtitle-card" :loading="subtitlesLoading">
            <a-empty
              v-if="!subtitlesLoading && !hasSubtitlePreview"
              :description="subtitlePreviewError || $t('video.subtitlePreviewEmpty')"
            />
            <a-tabs
              v-else-if="hasSubtitlePreview"
              v-model:activeKey="activeSubtitleTab"
              class="subtitle-tabs"
            >
              <a-tab-pane v-if="originalSubtitle" key="original" :tab="$t('video.originalSubtitle')">
                <div class="subtitle-content">
                  <div class="subtitle-header">
                    <span>{{ getLanguageText(project?.original_language) }}</span>
                  </div>
                  <div class="subtitle-preview">
                    <pre>{{ originalSubtitle }}</pre>
                  </div>
                </div>
              </a-tab-pane>

              <a-tab-pane
                v-for="lang in translatedPreviewLangs"
                :key="`translated-${lang}`"
                :tab="$t('video.translatedSubtitle', { lang: getLanguageText(lang) })"
              >
                <div class="subtitle-content">
                  <div class="subtitle-header">
                    <span>{{ getLanguageText(lang) }}</span>
                  </div>
                  <div class="subtitle-preview">
                    <pre>{{ translatedSubtitles[lang] }}</pre>
                  </div>
                </div>
              </a-tab-pane>
            </a-tabs>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <a-card :title="t('video.resultsDownloads.subtitleCardTitle')" class="download-card" :loading="loading">
      <a-space size="middle" wrap>
        <a-button
          type="primary"
          :loading="exportingSingle"
          :disabled="!primaryTargetLanguage"
          @click="downloadTranslatedVersion('single_line')"
        >
          <template #icon><DownloadOutlined /></template>
          {{ t('video.resultsDownloads.downloadSingleLine') }}
        </a-button>
        <a-button
          :loading="exportingMulti"
          :disabled="!primaryTargetLanguage"
          @click="downloadTranslatedVersion('multi_line')"
        >
          <template #icon><DownloadOutlined /></template>
          {{ t('video.resultsDownloads.downloadMultiLine') }}
        </a-button>
      </a-space>
      <p v-if="!primaryTargetLanguage" class="download-hint">
        {{ t('video.resultsDownloads.noTargetLanguage') }}
      </p>
    </a-card>

    <WorkItemReportCard
      :work-item-id="projectId"
      kind="video"
      :title="t('video.resultsDownloads.reportCardTitle')"
      :download-label="t('video.resultsDownloads.downloadReport')"
      :regenerate-label="t('video.resultsDownloads.regenerateReport')"
      :file-name-prefix="project?.title || 'video'"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { message } from 'ant-design-vue'
import {
  VideoCameraOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from '@ant-design/icons-vue'
import { useI18n } from 'vue-i18n'
import {
  getVideoWorkspace,
  type VideoWorkspaceView,
} from '@/modules/video/utils/video-workspace'
import { videoPlaybackUrl } from '@/modules/video/utils/video-playback-url'
import { subtitleApi } from '@/modules/video/api/subtitle'
import { exportTranslatedSubtitles } from '@/modules/video/api/subtitle-translation'
import {
  formatSubtitlesAsSrt,
  pickActiveSubtitleTab,
  unwrapSubtitleList,
} from '@/modules/video/utils/subtitle-preview'
import WorkItemReportCard from '@/modules/report/components/WorkItemReportCard.vue'

const { t } = useI18n()
const route = useRoute()

const loading = ref(false)
const subtitlesLoading = ref(false)
const exportingSingle = ref(false)
const exportingMulti = ref(false)
const project = ref<VideoWorkspaceView | null>(null)
const activeSubtitleTab = ref('original')
const originalSubtitle = ref('')
const translatedSubtitles = ref<Record<string, string>>({})
const subtitlePreviewError = ref('')

const projectId = parseInt(route.params.id as string)
const primaryTargetLanguage = computed(() => project.value?.target_languages?.[0] || '')
const translatedPreviewLangs = computed(() =>
  Object.keys(translatedSubtitles.value).filter((lang) => Boolean(translatedSubtitles.value[lang])),
)
const hasSubtitlePreview = computed(
  () => Boolean(originalSubtitle.value) || translatedPreviewLangs.value.length > 0,
)

onMounted(async () => {
  await loadProject()
  await loadSubtitles()
})

const loadProject = async () => {
  if (!projectId) {
    message.error(t('video.invalidProjectId'))
    return
  }
  loading.value = true
  try {
    project.value = await getVideoWorkspace(projectId)
  } catch (error) {
    console.error('Failed to load project:', error)
  } finally {
    loading.value = false
  }
}

const loadSubtitles = async () => {
  if (!project.value) return
  subtitlesLoading.value = true
  subtitlePreviewError.value = ''
  originalSubtitle.value = ''
  translatedSubtitles.value = {}
  try {
    const list = unwrapSubtitleList(await subtitleApi.getProjectSubtitles(projectId))
    originalSubtitle.value = formatSubtitlesAsSrt(list, 'original_text')
    const translated = formatSubtitlesAsSrt(list, 'translated_text')
    const langs = project.value.target_languages?.filter(Boolean) ?? []
    if (translated) {
      if (langs.length > 0) {
        translatedSubtitles.value = Object.fromEntries(langs.map((lang) => [lang, translated]))
      } else {
        translatedSubtitles.value = { translated }
      }
    }
    activeSubtitleTab.value = pickActiveSubtitleTab(
      Boolean(originalSubtitle.value),
      Object.keys(translatedSubtitles.value),
    )
  } catch (error) {
    console.error('Failed to load subtitles:', error)
    subtitlePreviewError.value = t('video.subtitlePreviewLoadFailed')
  } finally {
    subtitlesLoading.value = false
  }
}

const getVideoUrl = () => {
  if (!project.value?.has_video) return ''
  return videoPlaybackUrl(project.value)
}

const onVideoLoaded = () => {
  console.log('Video loaded successfully')
}

function triggerContentDownload(filename: string, content: string) {
  let mimeType = 'text/plain'
  if (filename.endsWith('.srt')) mimeType = 'text/srt'
  else if (filename.endsWith('.vtt')) mimeType = 'text/vtt'
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 100)
}

const downloadTranslatedVersion = async (version: 'single_line' | 'multi_line') => {
  const lang = primaryTargetLanguage.value
  if (!lang) {
    message.warning(t('video.resultsDownloads.noTargetLanguage'))
    return
  }
  const loadingRef = version === 'single_line' ? exportingSingle : exportingMulti
  loadingRef.value = true
  try {
    const response = await exportTranslatedSubtitles(projectId, {
      target_language: lang,
      format: 'srt',
      version,
      subtitle_type: 'translated',
    })
    const files = response?.files
    if (!Array.isArray(files) || files.length === 0) {
      message.warning(t('video.translationReviewPage.noFilesToDownload'))
      return
    }
    let count = 0
    for (const file of files) {
      if (file?.content && file?.filename) {
        triggerContentDownload(file.filename, file.content)
        count += 1
      }
    }
    if (count === 0) {
      message.warning(t('video.translationReviewPage.noFilesToDownload'))
    } else {
      message.success(t('video.translationReviewPage.exportSuccess', { count }))
    }
  } catch (error) {
    console.error('Export subtitle failed:', error)
    message.error(t('video.downloadSubtitleFailed'))
  } finally {
    loadingRef.value = false
  }
}

const getStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    draft: t('project.status.draft'),
    pending: t('project.status.pending'),
    processing: t('project.status.processing'),
    completed: t('project.status.completed'),
    failed: t('project.status.failed'),
    expired: t('project.status.expired'),
    cleaned: t('project.status.cleaned'),
    no_task: t('project.status.completed'),
  }
  return statusMap[status] || status
}

const getLanguageText = (lang?: string | null): string => {
  if (!lang) return '-'
  const languageMap: Record<string, string> = {
    zh: t('auth.language.chinese'),
    en: t('auth.language.english'),
    ja: t('auth.language.japanese'),
    ko: t('auth.language.korean'),
    fr: t('auth.language.french'),
    de: t('auth.language.german'),
    es: t('auth.language.spanish'),
    ru: t('auth.language.russian'),
    ar: t('auth.language.arabic'),
    pt: t('auth.language.portuguese'),
  }
  return languageMap[lang] || lang
}

const formatDuration = (seconds?: number | null): string => {
  if (!seconds) return '-'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString()
}
</script>

<style lang="less" scoped>
.video-results {
  padding: 24px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .header-left {
      .page-title {
        margin: 0 0 8px 0;
        font-size: 24px;
        font-weight: 600;
        color: #262626;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .page-description {
        margin: 0;
        color: #8c8c8c;
        font-size: 14px;
      }
    }
  }
}

.project-info-card {
  margin-bottom: 24px;

  .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
  }
}

.results-content {
  margin-bottom: 24px;

  .video-card,
  .subtitle-card {
    height: 500px;

    :deep(.ant-card-body) {
      height: calc(100% - 57px);
      padding: 16px;
    }
  }

  .video-card {
    overflow: hidden;

    :deep(.ant-card-body) {
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
  }

  .video-container {
    flex: 1;
    min-height: 0;
    width: 100%;
    background: #000;
    border-radius: 6px;
    overflow: hidden;

    .video-player {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #000;
    }
  }

  .subtitle-card {
    overflow: hidden;

    :deep(.ant-card-body) {
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    :deep(.ant-empty) {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    :deep(.subtitle-tabs) {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    :deep(.ant-tabs-nav) {
      flex-shrink: 0;
      margin-bottom: 8px;
    }

    :deep(.ant-tabs-content-holder),
    :deep(.subtitle-tabs > .ant-tabs-content) {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    :deep(.ant-tabs-content),
    :deep(.ant-tabs-tabpane) {
      height: 100%;
      overflow: hidden;
    }
  }

  .subtitle-content {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .subtitle-header {
      flex-shrink: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #f0f0f0;

      span {
        font-weight: 500;
        color: #262626;
      }
    }

    .subtitle-preview {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      background: #fafafa;
      border: 1px solid #d9d9d9;
      border-radius: 6px;
      padding: 12px;

      pre {
        margin: 0;
        font-family: 'Courier New', monospace;
        font-size: 12px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }
}

.download-card {
  margin-bottom: 16px;
}

.download-hint {
  margin: 12px 0 0;
  color: rgba(0, 0, 0, 0.45);
}
</style>
